
































import { mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import Vue, { PropType } from 'vue';
import LoadingSpinner from '@reciprocity/loading-spinner';
import Visualization from '../../../../pages/dashboards/visualizations/Visualization.vue';
import DashboardBlockEmptyState from './DashboardBlockEmptyState.vue';
import { DropdownOption } from '@/components/cards/types';
import { VISUALIZATIONS } from '@/constants/DashboardConstants';
import { mapBlockSettings } from '@/utils/DashboardUtils';
import { DimensionType } from '../types';
import {
  SERIES_BLOCK_QUERY,
  seriesVariables,
  seriesUpdate,
  isObjectAttributeValueSelected,
  isMappedObjectAttributeValueSelected,
  seriesFormattedAndFiltered,
} from './SeriesUtils';

export default Vue.extend({
  name: 'DashboardBlockPreview',
  components: {
    LoadingSpinner,
    Visualization,
    DashboardBlockEmptyState,
  },
  apollo: {
    series: {
      query: SERIES_BLOCK_QUERY,
      variables() {
        return seriesVariables(this.blockSettings);
      },
      skip() {
        return this.isRichText || !this.validData;
      },
      update(result) {
        return seriesUpdate(result, this.blockSettings);
      },
      error() {
        this.showAlert({
          type: 'error',
          message: 'Failed to load Dashboard Block data.',
        });
      },
    },
  },
  props: {
    blockId: {
      type: String,
      default: '',
    },
    selectedVisualization: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    selectedMetric: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    selectedDimension: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    selectedMappedObjectAttr: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    filters: {
      type: Array,
      default: () => [],
    },
    textContent: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      series: [],
    };
  },
  computed: {
    isRichText() {
      return this.blockSettings.visualization === VISUALIZATIONS.RICH_TEXT;
    },
    blockSettings() {
      return mapBlockSettings(
        this.selectedVisualization,
        this.selectedMetric,
        this.selectedDimension,
        this.selectedMappedObjectAttr,
        this.filters
      );
    },
    showVisualization() {
      if (this.isRichText) {
        return !isEmpty(this.textContent);
      }

      return this.series && this.series.length;
    },
    minRequiredFields() {
      const fields = this.isRichText
        ? {
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Rich Text': !isEmpty(this.textContent),
          }
        : {
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Object Type': !isEmpty(this.selectedMetric),
            'Attribute Type': !isEmpty(this.selectedDimension),
          };

      const required = Object.keys(fields).filter(f => !fields[f]);
      return !required.length;
    },
    emptyStateData() {
      if (!this.minRequiredFields) {
        return {
          title: '',
          desc: 'Fill the required fields to preview the chart.',
        };
      }

      return {
        title: 'Can’t find any data…',
        desc: 'Data for this dashboard seems to be missing.',
      };
    },
    validData() {
      const fields = this.isRichText
        ? {
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Rich Text': !isEmpty(this.textContent),
          }
        : {
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Object Type': !isEmpty(this.selectedMetric),
            'Attribute Type': !isEmpty(this.selectedDimension),
            Attribute: !(
              this.selectedDimension &&
              this.selectedDimension.type === DimensionType.MAPPING &&
              isEmpty(this.selectedMappedObjectAttr)
            ),
            [`${this.selectedMetric.label} Attribute Value`]: isObjectAttributeValueSelected(this.filters),
            [`${this.selectedMetric.label} Mapped Object Attribute Value`]: isMappedObjectAttributeValueSelected(
              this.filters
            ),
          };

      const required = Object.keys(fields).filter(f => !fields[f]);
      return !required.length;
    },
    seriesFormattedAndFiltered() {
      return seriesFormattedAndFiltered(this.blockSettings, this.series);
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
  },
});
